import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { t } from "@/lib/strings";
import { showToast } from "@/lib/toast";
import type { Memo, MemoEntryKind, MemoHeaderPayload, UUID } from "@/lib/types";

export function useMemoDetail(memoId: UUID | undefined) {
  return useQuery({
    queryKey: memoId ? queryKeys.memo(memoId) : ["memos", "none"],
    queryFn: () => api.getMemo(memoId as UUID),
    enabled: !!memoId,
    // Near-real-time: memos have no WebSocket yet, so poll every 5s while the
    // memo is open + on tab refocus. Background refetch (isFetching, not
    // isLoading) never re-shows the skeleton, and React Query structural-shares
    // unchanged data → no re-render / flicker when nothing changed. Paused while
    // the tab is hidden (refetchIntervalInBackground defaults false).
    refetchInterval: 5000,
    refetchOnWindowFocus: true,
  });
}

export function useSeriesMemos(seriesId: UUID | undefined) {
  return useQuery({
    queryKey: seriesId ? queryKeys.seriesMemos(seriesId) : ["meeting-series", "none", "memos"],
    queryFn: () => api.listSeriesMemos(seriesId as UUID),
    enabled: !!seriesId,
  });
}

export function useDeleteMemo(memoId: UUID, seriesId: UUID | undefined) {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => api.deleteMemo(memoId),
    onSuccess: () => {
      qc.removeQueries({ queryKey: queryKeys.memo(memoId) });
      if (seriesId) {
        void qc.invalidateQueries({ queryKey: queryKeys.seriesMemos(seriesId) });
        void qc.invalidateQueries({ queryKey: queryKeys.series(seriesId) });
      }
      void qc.invalidateQueries({ queryKey: queryKeys.meetingSeries });
    },
  });
}

export function useMemoAudit(memoId: UUID | undefined, enabled: boolean) {
  return useQuery({
    queryKey: memoId ? queryKeys.memoAudit(memoId) : ["memos", "none", "audit"],
    queryFn: () => api.getMemoAudit(memoId as UUID),
    enabled: !!memoId && enabled,
  });
}

export function useMemoSectionHistory(
  memoId: UUID | undefined,
  anchorId: UUID | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey:
      memoId && anchorId
        ? queryKeys.memoSectionHistory(memoId, anchorId)
        : ["memos", "none", "section-history"],
    queryFn: () => api.getMemoSectionHistory(memoId as UUID, anchorId as UUID),
    enabled: !!memoId && !!anchorId && enabled,
  });
}

/**
 * All memo mutations for a given memo. Endpoints that return the full Memo write
 * it straight into the cache; entry-level ones (which return a MemoEntry) just
 * invalidate the memo query so the nested blocks refetch.
 */
export function useMemoMutations(memoId: UUID) {
  const qc = useQueryClient();
  const setMemo = (memo: Memo) => qc.setQueryData(queryKeys.memo(memoId), memo);
  // For changes the series' Protokolle list shows (date/time, status, participant
  // count): without this, going back to the Reihe within the 30s staleTime shows the
  // old Anstehend/Vergangen split and badge.
  const setMemoAndList = (memo: Memo) => {
    setMemo(memo);
    void qc.invalidateQueries({ queryKey: queryKeys.seriesMemos(memo.series_id) });
  };
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: queryKeys.memo(memoId) });
    void qc.invalidateQueries({ queryKey: queryKeys.memoAudit(memoId) });
  };

  const patchHeader = useMutation({
    mutationFn: (payload: Partial<MemoHeaderPayload> & { title?: string }) =>
      api.patchMemo(memoId, payload),
    onSuccess: setMemoAndList,
  });
  const setParticipants = useMutation({
    mutationFn: (participant_ids: UUID[]) => api.setMemoParticipants(memoId, participant_ids),
    onSuccess: setMemoAndList,
  });
  const finalize = useMutation({
    mutationFn: () => api.finalizeMemo(memoId),
    onSuccess: setMemoAndList,
  });
  const reopen = useMutation({
    mutationFn: () => api.reopenMemo(memoId),
    onSuccess: setMemoAndList,
  });
  const requestPrep = useMutation({ mutationFn: () => api.requestMemoPrep(memoId) });

  const addBlock = useMutation({
    mutationFn: (title: string) => api.addMemoBlock(memoId, title),
    onSuccess: setMemo,
  });
  const patchBlock = useMutation({
    mutationFn: (v: { blockId: UUID; title?: string }) =>
      api.patchMemoBlock(v.blockId, { title: v.title }),
    onSuccess: setMemo,
  });
  const moveBlock = useMutation({
    mutationFn: (v: { blockId: UUID; direction: "up" | "down" }) =>
      api.moveMemoBlock(v.blockId, v.direction),
    // A 5s poll in flight may have read BEFORE the move committed; landing after the
    // move's response it would paint the old order back for one poll. Cancel at BOTH
    // ends: onMutate drops polls already running, onSuccess drops any that the interval
    // started while the move was pending (TanStack lets a fetch success overwrite a
    // manual setQueryData).
    onMutate: () => qc.cancelQueries({ queryKey: queryKeys.memo(memoId) }),
    onSuccess: async (memo) => {
      await qc.cancelQueries({ queryKey: queryKeys.memo(memoId) });
      setMemo(memo);
      void qc.invalidateQueries({ queryKey: queryKeys.memoAudit(memoId) });
    },
    onError: () => {
      showToast(t.memoMoveBlockFailed, "error");
      void qc.invalidateQueries({ queryKey: queryKeys.memo(memoId) });
    },
  });
  const deleteBlock = useMutation({
    mutationFn: (blockId: UUID) => api.deleteMemoBlock(blockId),
    onSuccess: setMemo,
    // A refused or lost delete was silent: the Abschnitt simply stayed. Say so, and refetch so
    // the memo shows what is stored.
    onError: () => {
      showToast(t.memoDeleteBlockFailed, "error");
      void qc.invalidateQueries({ queryKey: queryKeys.memo(memoId) });
    },
  });

  const addEntry = useMutation({
    mutationFn: (v: {
      blockId: UUID;
      body?: string;
      afterEntryId?: UUID;
      kind?: MemoEntryKind;
      logDate?: string | null;
    }) =>
      api.addMemoEntry(memoId, {
        block_id: v.blockId,
        body: v.body,
        after_entry_id: v.afterEntryId,
        kind: v.kind,
        log_date: v.logDate,
      }),
    onSuccess: invalidate,
  });
  const patchEntry = useMutation({
    // `quiet`: the caller reports a failure itself (the undo of a delete says
    // "Wiederherstellen fehlgeschlagen."), so the hook must not toast a second time.
    mutationFn: (v: {
      entryId: UUID;
      body?: string;
      position?: number;
      block_id?: UUID;
      quiet?: boolean;
    }) =>
      api.patchMemoEntry(v.entryId, { body: v.body, position: v.position, block_id: v.block_id }),
    onSuccess: invalidate,
    // 🔴 A failed save was SILENT: the editor closes on commit, the next poll shows the stored
    // body again, and the typed text was gone without a word (a deadlock-killed PATCH did exactly
    // that). One toast per failed save, and a save goes out once per edit session, never per
    // keystroke or pause: a point's editor dedupes its blur and Ctrl+Enter (MemoEntryRow's
    // committedRef) and ignores its own exit while Backspace is deleting it (leavingRef); a
    // Zwischenüberschrift dedupes Enter and blur (MemoSubheadingRow's sentRef).
    onError: (_error, v) => {
      if (!v.quiet) showToast(t.memoPatchEntryFailed, "error");
      void qc.invalidateQueries({ queryKey: queryKeys.memo(memoId) });
    },
  });
  const deleteEntry = useMutation({
    // 🔴 A 404 means the point is already gone (a colleague deleted it a moment earlier). That is
    // what the caller asked for, and "Bitte erneut versuchen" cannot help: a retry 404s again. So
    // it counts as deleted, with no toast; the answer is null and the memo is refetched instead.
    // Resolving also lets the callers go on as after a delete: Backspace on an empty point focuses
    // the previous one, and the undo may re-create the line (it no longer exists: no duplicate).
    mutationFn: async (entryId: UUID): Promise<Memo | null> => {
      try {
        return await api.deleteMemoEntry(entryId);
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
    onSuccess: (memo) => {
      if (memo) setMemo(memo);
      else void qc.invalidateQueries({ queryKey: queryKeys.memo(memoId) });
    },
    onError: () => {
      showToast(t.memoDeleteEntryFailed, "error");
      void qc.invalidateQueries({ queryKey: queryKeys.memo(memoId) });
    },
  });
  const moveEntry = useMutation({
    mutationFn: (v: { entryId: UUID; blockId: UUID; subheadingId?: UUID | null }) =>
      api.moveMemoEntry(v.entryId, v.blockId, v.subheadingId),
    // Same two-ended cancel as moveBlock: a 5s poll that read BEFORE the move committed
    // must not paint the point back into its old section after the response landed.
    onMutate: () => qc.cancelQueries({ queryKey: queryKeys.memo(memoId) }),
    onSuccess: async (memo) => {
      await qc.cancelQueries({ queryKey: queryKeys.memo(memoId) });
      setMemo(memo);
      void qc.invalidateQueries({ queryKey: queryKeys.memoAudit(memoId) });
    },
    // e.g. 409: the Zwischenüberschrift was closed or deleted meanwhile.
    onError: () => {
      showToast(t.memoMoveEntryFailed, "error");
      void qc.invalidateQueries({ queryKey: queryKeys.memo(memoId) });
    },
  });
  const setEntryLogDate = useMutation({
    mutationFn: (v: { entryId: UUID; logDate: string | null }) =>
      api.setMemoEntryLogDate(v.entryId, v.logDate),
    onSuccess: invalidate,
  });
  const setDue = useMutation({
    mutationFn: (v: { entryId: UUID; due_date: string | null }) =>
      api.setEntryDue(v.entryId, v.due_date),
    onSuccess: invalidate,
  });
  const completeTask = useMutation({
    mutationFn: (v: { entryId: UUID; solution: string | null }) =>
      api.completeEntryTask(v.entryId, v.solution),
    onSuccess: invalidate,
  });
  const reopenTask = useMutation({
    mutationFn: (entryId: UUID) => api.reopenEntryTask(entryId),
    onSuccess: invalidate,
  });
  const retireEntry = useMutation({
    // `quiet`: the caller reports a failure itself — the undo of a delete re-applies
    // "abgeschlossen" and says "Wiederherstellen fehlgeschlagen." once for the whole undo.
    mutationFn: (v: { entryId: UUID; retired: boolean; quiet?: boolean }) =>
      api.retireEntry(v.entryId, v.retired),
    onSuccess: invalidate,
    // e.g. 409: the point or its Zwischenüberschrift was deleted while the close waited for the
    // memo lock. Say so, and refetch so the row shows what is stored.
    onError: (_error, v) => {
      if (!v.quiet) showToast(t.memoRetireFailed, "error");
      void qc.invalidateQueries({ queryKey: queryKeys.memo(memoId) });
    },
  });

  return {
    patchHeader,
    setParticipants,
    finalize,
    reopen,
    requestPrep,
    addBlock,
    patchBlock,
    moveBlock,
    deleteBlock,
    addEntry,
    patchEntry,
    deleteEntry,
    moveEntry,
    setEntryLogDate,
    setDue,
    completeTask,
    reopenTask,
    retireEntry,
  };
}

export type MemoMutations = ReturnType<typeof useMemoMutations>;
